import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import useApi from "../../hooks/useApi";

/**
 * EmailCampaignAddModal component.
 *
 * This modal component allows users to add a new email campaign. It provides a form with validation for campaign details.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.config - Configuration object containing API endpoints.
 * @param {boolean} props.isOpen - Flag indicating if the modal is open.
 * @param {Function} props.onChangeBulk - Callback function to update parent component state.
 * @returns {JSX.Element} The rendered EmailCampaignAddModal component.
 */
const EmailCampaignAddModal = (props) => {
	const [form, setForm] = useState({
		values: {
			name: '',
			description: '',
			emailList: '',
			startDate: null,
			endDate: null,
			status: '',
			campaignType: '',
		},
		errors: {},
	});

	const { config, isOpen, onChangeBulk: parentOnChangeBulk } = props;
	const api = useApi();

	const handleChange = (field, value) => {
		setForm((prev) => ({
			...prev,
			values: { ...prev.values, [field]: value },
			errors: { ...prev.errors, [field]: '' },
		}));
	};

	const onResetFormErrors = () => {
		setForm((prev) => ({
			...prev,
			errors: {},
		}));
	};

	const onInvalidForm = (errors) => {
		setForm((prev) => ({ ...prev, errors }));
		return;
	};

	const handleClose = () => {
		onResetFormErrors();
		setForm((prev) => ({
			...prev,
			values: {
				name: '',
				description: '',
				emailList: '',
				startDate: null,
				endDate: null,
				status: '',
				campaignType: '',
			},
		}));
		// Reset local form values on close
		onChangeBulk({
			name: '',
			description: '',
			emailList: '',
			startDate: '',
			endDate: '',
			status: '',
			campaignType: '',
		});
		parentOnChangeBulk({ emailCampaignAddModalIsOpen: false });
	};

	const handleSubmit = async () => {
		const errors = {};
		if (!form.values.name) errors.name = "Campaign name is required";
		if (!form.values.emailList) errors.emailList = "Email list is required";
		if (!form.values.startDate) errors.startDate = "Start date is required";
		if (!form.values.endDate) errors.endDate = "End date is required";
		if (!form.values.status) errors.status = "Status is required";
		if (!form.values.campaignType) errors.campaignType = "Campaign type is required";

		if (!isEmpty(errors)) return onInvalidForm(errors);

		const data = {
			name: form.values.name,
			description: form.values.description,
			emailList: form.values.emailList.split(",").map((email) => email.trim()),
			startDate: form.values.startDate ? form.values.startDate.toISOString() : "",
			endDate: form.values.endDate ? form.values.endDate.toISOString() : "",
			status: form.values.status,
			campaignType: form.values.campaignType,
		};

		await api.emailCampaigns.create({ data: data });

		setForm((prev) => ({
			...prev,
			values: {
				name: '',
				description: '',
				emailList: '',
				startDate: null,
				endDate: null,
				status: '',
				campaignType: '',
			},
		}));

		parentOnChangeBulk({
			emailCampaignAddModalIsOpen: false,
		});
	};

	return (
		<Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
			<DialogTitle>Add New Email Campaign</DialogTitle>
			<DialogContent>
				<TextField
					label="Campaign Name"
					fullWidth
					margin="normal"
					value={form.values.name}
					error={!!form.errors.name}
					helperText={form.errors.name}
					onChange={(e) => handleChange("name", e.target.value)}
				/>
				<TextField
					label="Description"
					fullWidth
					margin="normal"
					multiline
					value={form.values.description}
					error={!!form.errors.description}
					helperText={form.errors.description}
					onChange={(e) => handleChange("description", e.target.value)}
				/>
				<TextField
					label="Email List (comma separated)"
					fullWidth
					margin="normal"
					value={form.values.emailList}
					error={!!form.errors.emailList}
					helperText={form.errors.emailList}
					onChange={(e) => handleChange("emailList", e.target.value)}
				/>
				<LocalizationProvider dateAdapter={AdapterDateFns}>
					<DatePicker
						label="Start Date"
						value={form.values.startDate}
						onChange={(newValue) => handleChange("startDate", newValue)}
						renderInput={(params) => (
							<TextField
								{...params}
								fullWidth
								margin="normal"
								error={!!form.errors.startDate}
								helperText={form.errors.startDate}
							/>
						)}
					/>
					<DatePicker
						label="End Date"
						value={form.values.endDate}
						onChange={(newValue) => handleChange("endDate", newValue)}
						renderInput={(params) => (
							<TextField
								{...params}
								fullWidth
								margin="normal"
								error={!!form.errors.endDate}
								helperText={form.errors.endDate}
							/>
						)}
					/>
				</LocalizationProvider>
				<TextField
					label="Status"
					fullWidth
					margin="normal"
					value={form.values.status}
					error={!!form.errors.status}
					helperText={form.errors.status}
					onChange={(e) => handleChange("status", e.target.value)}
				/>
				<TextField
					label="Campaign Type"
					fullWidth
					margin="normal"
					value={form.values.campaignType}
					error={!!form.errors.campaignType}
					helperText={form.errors.campaignType}
					onChange={(e) => handleChange("campaignType", e.target.value)}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} color="secondary">
					Cancel
				</Button>
				<Button onClick={handleSubmit} color="primary">
					Submit
				</Button>
			</DialogActions>
		</Dialog>
	);
};

EmailCampaignAddModal.propTypes = {
	config: PropTypes.shape({
		uri: PropTypes.shape({
			base: PropTypes.string.isRequired,
		}).isRequired,
	}).isRequired,
	isOpen: PropTypes.bool.isRequired,
	onChangeBulk: PropTypes.func.isRequired,
};

export default EmailCampaignAddModal;
